import React, { FC, useEffect, useState } from 'react';
import classes from './WinnersDialog.module.scss';
import inviteClasses from './InviteDialog.module.scss';
import { useDispatch } from 'react-redux';
import { GameConfigDTO, InviteMessageDTO } from '@envclient/envcore/src/types/core';
import { useInterval } from 'react-use';
import bell from '../assets/bell3.mp3';
import Button from './Button';
import Modal from './Modal';
import { useAppSelector } from '../reducers/store';
import { isSupported } from '../utils/game';
import WsConnection from '../service/WsConnection';
import { consumeInvitation } from '../reducers/invitationReducer';
import { useNewUser } from '../hooks/useNewUser';

const InviteDialog: FC<Props> = ({ invite, onClose }) => {
  const dispatch = useDispatch();
  const [count, setCount] = useState(0);
  const interval = 1000;
  const inviteTimeout = 30;

  const meUser = useAppSelector((state) => state.user);
  const gameList = useAppSelector((state) => state.gameList);

  const gameRoom = gameList.find((g) => g.room.roomId === invite.room.roomId);
  const flatGameConfigs = Object.values(useAppSelector((state) => state.gameConfigs)).flat();

  const handleAccept = () => {
    // TODO -invite shouldnt even appear if not supported
    const roomGameConfig = flatGameConfigs.find((g) => g.type === gameRoom?.gameType);
    if (!isSupported(roomGameConfig, window.appConfig.version)) return;
    WsConnection.send({ type: 'JOIN', room: invite.room });
    dispatch(consumeInvitation(invite));
    onClose();
  };

  const handleDeny = () => {
    dispatch(consumeInvitation(invite));
    onClose();
  };

  useInterval(
    () => {
      setCount(count + 1);
    },
    count < inviteTimeout ? interval : null
  );

  useEffect(() => {
    if (count === inviteTimeout) {
      dispatch(consumeInvitation(invite));
      onClose();
    }
  }, [count, invite, dispatch, onClose]);

  useEffect(() => {
    if (count === 5 && meUser?.type === 'bot') {
      WsConnection.send({ type: 'JOIN', room: invite.room });
      dispatch(consumeInvitation(invite));
      onClose();
    }
  }, [count, invite, dispatch, onClose, meUser]);

  const isNewUser = useNewUser();

  useEffect(() => {
    const roomGameConfig = flatGameConfigs.find((g) => g.type === gameRoom?.gameType);
    if (!isSupported(roomGameConfig, window.appConfig.version)) return;

    if (invite) playSound();
  }, [gameRoom?.room.roomId]);

  // gameRoom is looked up from a separately-arriving SET_GAME_LIST broadcast, so right after an
  // invite for a brand-new room it can briefly be undefined even for a perfectly valid invite - give
  // that race a few seconds before concluding it's genuinely unsupported/unknown. Consuming here
  // either way matters: invitationReducer only ever holds ONE pending invite and silently drops any
  // new one while a slot is occupied, so if this invite were never consumed (the old behavior just
  // rendered null and left it in state forever), it would permanently block every future invite for
  // the rest of this session - which is exactly the "invites sometimes just stop working" symptom.
  useEffect(() => {
    if (count < 3) return;
    const roomGameConfig = flatGameConfigs.find((g) => g.type === gameRoom?.gameType);
    if (isSupported(roomGameConfig, window.appConfig.version)) return;
    dispatch(consumeInvitation(invite));
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count, gameRoom, invite, dispatch, onClose]);

  const playSound = () => {
    const audio = new Audio(bell);
    audio.play();
  };

  const roomGameConfig = flatGameConfigs.find((g) => g.type === gameRoom?.gameType);
  if (!isSupported(roomGameConfig, window.appConfig.version)) return null;

  return (
    <Modal>
      <div className={classes.mainCont}>
        <div className={classes.bg}></div>
        <div className={classes.panel}>
          <div className={classes.header}>
            <div className={classes.title}>Invite</div>
          </div>
          <div className={classes.body + ' ' + inviteClasses.body}>
            <p className={classes.winners}>
              {invite.fromUser.displayName} has invited you to {invite.room.roomName}.
              {gameRoom && (
                <>
                  <br /> Speed: {gameRoom?.delay / 1000} sec / Max. players: {gameRoom?.maxPlayer}
                </>
              )}
              <br /> Do you accept?
              <br />
              Invite expires in {inviteTimeout - count} seconds...
            </p>
          </div>
          <div className={classes.btnBar}>
            <Button onClick={handleDeny}>No thanks</Button>
            <Button onClick={handleAccept}>OK</Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

interface Props {
  invite: InviteMessageDTO;
  onClose: () => void;
}
export default InviteDialog;
